'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { clientEnvs } from '@/config';
import { isSafeAppEnvironment } from '@/helpers/iframe';
import { trackAnalyticsEvent } from '@/lib/analytics/client/track';
import { logger } from '@/lib/observability/logger';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';

// Web HID / Web Bluetooth are unavailable on mobile, so the button can't work there.
const isMobileBrowser = () => typeof navigator !== 'undefined' && /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent);

// Ledger's staging env is VPN-internal; "Morpho" lives on prod, so every env uses it.
const LEDGER_ENVIRONMENT = 'production';
const LEDGER_INIT_ERROR_MESSAGES = ['Failed to get DApp config'];
const isLedgerInitError = (reason: unknown): reason is Error => reason instanceof Error && LEDGER_INIT_ERROR_MESSAGES.includes(reason.message);

// Stale deployments and crawlers fail to fetch the SDK's lazy chunks; not a Ledger failure.
const CHUNK_LOAD_ERROR_MESSAGE = /^(Failed to load chunk|Loading (CSS )?chunk)/;
const isChunkLoadError = (error: unknown): error is Error => error instanceof Error && (error.name === 'ChunkLoadError' || CHUNK_LOAD_ERROR_MESSAGE.test(error.message));

// The SDK injects this Google Fonts stylesheet, which our CSP blocks; globals.css serves Inter
// instead. It skips injection when a link with this href exists, and a link without `rel` fetches nothing.
export const LEDGER_GOOGLE_FONTS_URL = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';
const preventLedgerFontInjection = () => {
  if (document.querySelector(`link[href="${LEDGER_GOOGLE_FONTS_URL}"]`)) return;
  const placeholder = document.createElement('link');
  placeholder.href = LEDGER_GOOGLE_FONTS_URL;
  document.head.appendChild(placeholder);
};
const loadLedgerStyles = () => import('@ledgerhq/ledger-wallet-provider/styles.css');
const loadLedgerProvider = () => import('@ledgerhq/ledger-wallet-provider');

/**
 * Mounts the Ledger Wallet Provider — an EIP-6963 software wallet auto-discovered
 * by Reown AppKit. Client-only, gated behind `ledgerWalletButton`, renders nothing.
 *
 * @see https://developers.ledger.com/docs/ledger-wallet-provider/overview
 */
const LedgerWalletProvider = () => {
  const $ = _c(3);
  const {
    ledgerWalletButton: isEnabled
  } = useFeatureFlags();
  let t0;
  let t1;
  if ($[0] !== isEnabled) {
    t0 = () => {
      if (!isEnabled || isMobileBrowser() || isSafeAppEnvironment()) {
        return;
      }
      let cleanup;
      let cancelled = false;
      const onUnhandledRejection = _temp;
      window.addEventListener("unhandledrejection", onUnhandledRejection);
      const onModalOpen = _temp2;
      window.addEventListener("ledger-core-modal-open", onModalOpen);
      (async () => {
        ;
        try {
          await loadLedgerStyles();
          const {
            initializeLedgerProvider
          } = await loadLedgerProvider();
          if (cancelled) {
            return;
          }
          preventLedgerFontInjection();
          cleanup = initializeLedgerProvider({
            dAppIdentifier: "morpho",
            apiKey: clientEnvs.NEXT_PUBLIC_LEDGER_API_KEY,
            environment: LEDGER_ENVIRONMENT,
            loggerLevel: "error",
            walletTransactionFeatures: ["send", "receive", "swap", "buy", "sell"],
            floatingButtonPosition: "bottom-left"
          });
          trackAnalyticsEvent("ledger_button_shown", {});
        } catch (t2) {
          const error = t2;
          if (isChunkLoadError(error)) {
            logger.warn("Ledger Wallet Provider chunk failed to load", {
              extra: {
                cause: error.message
              }
            });
            return;
          }
          logger.error(new Error("Failed to initialize Ledger Wallet Provider", {
            cause: error
          }));
        }
      })();
      return () => {
        cancelled = true;
        window.removeEventListener("unhandledrejection", onUnhandledRejection);
        window.removeEventListener("ledger-core-modal-open", onModalOpen);
        cleanup?.();
      };
    };
    t1 = [isEnabled];
    $[0] = isEnabled;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return null;
};
export default LedgerWalletProvider;
function _temp(event) {
  if (!isLedgerInitError(event.reason)) {
    return;
  }
  event.preventDefault();
  logger.warn("Ledger Wallet Provider context initialization failed", {
    extra: {
      cause: event.reason
    }
  });
  trackAnalyticsEvent("ledger_button_init_failed", {});
}
function _temp2() {
  return trackAnalyticsEvent("ledger_modal_opened", {});
}